import {PlainMessage, Struct, Timestamp, toPlainMessage} from '@bufbuild/protobuf'
import {Code, ConnectError} from '@connectrpc/connect'
import {resolveHypermediaUrl, resolveId} from '@seed-hypermedia/client'
import {
  HMAccountsMetadata,
  HMDocumentInfo,
  HMDocumentInfoSchema,
  HMDocumentMetadataSchema,
  HMDomainInfo,
  HMGetCIDOutput,
  HMListAccountsOutput,
  HMListCommentsByAuthorOutput,
  HMListEventsOutput,
  HMMetadata,
  HMMetadataPayload,
  HMResolvedResource,
  HMResource,
  HMTimestamp,
  HMTimestampSchema,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {useInfiniteQuery, useQueries, useQuery, useQueryClient, UseQueryOptions} from '@tanstack/react-query'
import {useEffect, useMemo, useRef, useState} from 'react'
import {DocumentInfo, RedirectErrorDetails} from '../client'
import {DISCOVERY_TIMEOUT_MS} from '../constants'
import {useDraftsForAccountSafe} from '../draft-breadcrumb-context'
import {MAX_REDIRECT_HOPS} from '../redirects'
import {useUniversalAppContext, useUniversalClient} from '../routing'
import {useStream} from '../use-stream'
import {createWebHMUrl, entityQueryPathToHmIdPath, extractViewTermFromUrl, hmId, latestId, unpackHmId} from '../utils'
import {isPendingSpaceUid} from '../utils/pending-space'
import {
  queryAccount,
  queryCapabilities,
  queryChanges,
  queryCitations,
  queryComments,
  queryDirectory,
  queryDocumentCollaborators,
  queryDomain,
  queryRawResource,
  queryResource,
} from './queries'
import {queryKeys} from './query-keys'

export function documentMetadataParseAdjustments(metadata: any) {
  if (metadata?.theme === '[object Object]') {
    metadata.theme = undefined
  }
}

function prepareHMDocumentMetadata(metadata: Struct | undefined): HMMetadata {
  const docMeta = metadata?.toJson({emitDefaultValues: true, enumAsInteger: false}) || {}
  documentMetadataParseAdjustments(docMeta)
  return HMDocumentMetadataSchema.parse(docMeta)
}

function prepareHMDate(date: PlainMessage<Timestamp> | undefined): HMTimestamp | undefined {
  if (!date) return undefined
  const d = toPlainMessage(date)
  return HMTimestampSchema.parse(d)
}

export function prepareHMDocumentInfo(doc: DocumentInfo): HMDocumentInfo {
  const docInfo = toPlainMessage(doc)
  const path = entityQueryPathToHmIdPath(docInfo.path)
  const createTime = prepareHMDate(docInfo.createTime)
  let sortTime: Date
  if (!createTime) {
    sortTime = new Date(0)
  } else if (typeof createTime === 'string') {
    sortTime = new Date(createTime)
  } else {
    sortTime = new Date(Number(createTime.seconds) * 1000 + createTime.nanos / 1000000)
  }

  // Transform redirectInfo from proto format to frontend format
  let redirectInfo
  if (docInfo.redirectInfo) {
    const target = `${docInfo.redirectInfo.account}${docInfo.redirectInfo.path ? `/${docInfo.redirectInfo.path}` : ''}`
    redirectInfo = {
      type: 'redirect' as const,
      target,
      republish: docInfo.redirectInfo.republish,
    }
  }

  return HMDocumentInfoSchema.parse({
    ...docInfo,
    metadata: prepareHMDocumentMetadata(doc.metadata),
    isCollection: docInfo.isCollection,
    type: 'document',
    createTime,
    updateTime: prepareHMDate(docInfo.updateTime),
    sortTime,
    id: hmId(docInfo.account, {path, version: docInfo.version, latest: true}),
    path,
    redirectInfo,
  } as const)
}

export function useDiscoveryState(entityId: string | undefined) {
  const client = useUniversalClient()
  const stream = entityId ? client.discovery?.getDiscoveryStream(entityId) : undefined
  const discoveryState = useStream(stream)

  // Check if discovery has timed out
  const isTimedOut = discoveryState ? Date.now() - discoveryState.startedAt > DISCOVERY_TIMEOUT_MS : false

  return {
    isDiscovering: discoveryState?.isDiscovering && !isTimedOut,
    isTimedOut,
    isTombstone: discoveryState?.isTombstone ?? false,
    isNotFound: discoveryState?.isNotFound ?? false,
  }
}

export function useResource(
  id: UnpackedHypermediaId | null | undefined,
  options?: UseQueryOptions<HMResource | null> & {
    subscribed?: boolean
    followRedirects?: boolean
    recursive?: boolean
    onRedirectOrDeleted?: (opts: {isDeleted: boolean; redirectTarget: UnpackedHypermediaId | null}) => void
  },
) {
  const client = useUniversalClient()
  const {subscribed, recursive, followRedirects, onRedirectOrDeleted, ...queryOptions} = options ?? {}

  // Discovery subscription (desktop only)
  useEffect(() => {
    if (!subscribed || !id || !client.subscribeEntity) {
      return
    }
    const cleanup = client.subscribeEntity({id, recursive})
    return () => {
      cleanup()
    }
  }, [subscribed, recursive, id?.id, client.subscribeEntity])

  const result = useQuery({
    ...queryResource(client, id, {followRedirects}),
    ...queryOptions,
  })

  // Get discovery state - only subscribe when entity is subscribed for discovery.
  // Non-subscribed callers don't need discovery state and subscribing would cause
  // unnecessary re-renders (e.g. 500 CommentContent components sharing same account stream).
  const {
    isDiscovering: discoveryInProgress,
    isTombstone,
    isNotFound,
  } = useDiscoveryState(subscribed ? id?.id : undefined)

  // Determine if we should show discovering UI
  // Show discovering when: subscribed, not-found, AND either discovery in progress OR query is fetching
  // BUT: never show discovering UI for tombstoned or settled not-found resources
  // The isFetching check covers the gap between discovery completion and data arrival
  // discoveryInProgress === undefined means we haven't received discovery state yet (the
  // subscription round-trip hasn't completed). Since we're subscribed and discovery will start,
  // treat this gap as discovering rather than flashing "not found" prematurely.
  // However, on web there is no discovery service, so discoveryInProgress is permanently
  // undefined — don't treat that as "discovering" or profiles will never resolve.
  const hasDiscoveryService = !!client.discovery
  const isDiscovering =
    !!subscribed &&
    !isTombstone &&
    !isNotFound && // Don't show loading when discovery determined not-found
    result.data?.type === 'not-found' &&
    (!!discoveryInProgress || result.isFetching || (hasDiscoveryService && discoveryInProgress === undefined))

  // Redirect handling — queryResource follows redirects, so data.type is never
  // 'redirect'. Instead, detect redirects by comparing the returned id with the
  // requested id. Also populate the cache for the target id so other components
  // querying the target directly get an instant cache hit.
  const queryClient = useQueryClient()
  const redirectTarget = (() => {
    if (!result.data || !id) return null
    if (result.isPreviousData) return null
    if (result.data.type !== 'document' && result.data.type !== 'comment') return null
    if (result.data.id?.id !== id.id) return result.data.id
    return null
  })()
  const onRedirectOrDeletedRef = useRef(onRedirectOrDeleted)
  onRedirectOrDeletedRef.current = onRedirectOrDeleted
  const requestedId = id?.id || null
  const resolvedId = redirectTarget?.id || null
  useEffect(() => {
    if (!redirectTarget || !requestedId || !resolvedId) return
    // Cache the resolved data under the target's key so other components
    // querying the target id get an instant cache hit instead of re-fetching.
    queryClient.setQueryData(
      [queryKeys.ENTITY, redirectTarget.id, redirectTarget.version || undefined, redirectTarget.latest || false],
      result.data,
    )
    onRedirectOrDeletedRef.current?.({isDeleted: false, redirectTarget})
  }, [queryClient, requestedId, resolvedId])

  return {
    ...result,
    isDiscovering,
    isTombstone,
  }
}

/**
 * Fetches a resource WITHOUT following redirects.
 *
 * `useResource` auto-follows redirects, so its `data.type` is never 'redirect' —
 * a redirected profile/document resolves straight to its target. This hook
 * instead returns the raw resource, so `data.type` can be 'redirect',
 * 'tombstone', 'not-found', or 'error'. Inspector UIs (e.g. the Explore app)
 * use it to display the redirect itself and link to the destination rather than
 * navigating there automatically.
 */
export function useRawResource(
  id: UnpackedHypermediaId | null | undefined,
  options?: UseQueryOptions<HMResource | null>,
) {
  const client = useUniversalClient()
  return useQuery({
    ...queryRawResource(client, id),
    ...options,
  })
}

/**
 * Determines whether the given ID is viewing the latest version of a document.
 * Always compares actual document versions from the server rather than
 * relying on URL parameters.
 *
 * Returns `true` while data is loading (to avoid banner flash).
 */
export function useIsLatest(
  id: UnpackedHypermediaId | null | undefined,
  currentResource?: ReturnType<typeof useResource>,
): boolean {
  const latestResource = useResource(id ? latestId(id) : null, {subscribed: true})

  if (!id) return true

  // While latest is loading, default to true to prevent banner flash
  if (latestResource.isLoading) return true

  if (!latestResource.data || latestResource.data.type !== 'document') return true

  // Prefer the version from the already-fetched resource data over the URL id
  const currentVersion = currentResource?.data?.type === 'document' ? currentResource.data.document.version : id.version

  if (!currentVersion) return true

  return latestResource.data.document.version === currentVersion
}

export function useAccount(
  id: string | null | undefined,
  options?: UseQueryOptions<HMMetadataPayload | null> & {subscribe?: boolean},
) {
  const client = useUniversalClient()
  const {subscribe, ...queryOptions} = options ?? {}

  useAccountSubscriptions(subscribe && id ? [id] : [])

  return useQuery({
    ...queryAccount(client, id),
    ...queryOptions,
  })
}

/**
 * Subscribes to account profile blobs without reading discovery state.
 */
export function useAccountSubscriptions(accountUids: string[]) {
  const client = useUniversalClient()
  const idsKey = useMemo(() => [...accountUids].sort().join(','), [accountUids])
  const previousIdsKeyRef = useRef('')
  const accountIdsRef = useRef<ReturnType<typeof hmId>[]>([])

  const accountIds = useMemo(() => {
    if (previousIdsKeyRef.current !== idsKey) {
      previousIdsKeyRef.current = idsKey
      accountIdsRef.current = accountUids.map((uid) => hmId(uid))
    }
    return accountIdsRef.current
  }, [accountUids, idsKey])

  useEffect(() => {
    if (!client.subscribeEntity) return
    const cleanups = accountIds.map((id) => client.subscribeEntity!({id, scope: 'profile'}))
    return () => cleanups.forEach((cleanup) => cleanup())
  }, [accountIds, client.subscribeEntity])
}

/**
 * Returns tracked domain information via the universal client API.
 * Use `forceCheck` to refresh the domain cache before comparing ownership.
 */
export function useDomain(
  domain: string | null | undefined,
  options?: UseQueryOptions<HMDomainInfo | null> & {forceCheck?: boolean},
) {
  const client = useUniversalClient()
  const forceCheck = options?.forceCheck ?? false
  const {forceCheck: _forceCheck, ...queryOptions} = options ?? {}
  return useQuery({
    ...queryDomain(client, domain, forceCheck),
    ...queryOptions,
  })
}

/** Resolves domain-based HM references before they reach account-based resource and discovery APIs. */
export function useResolvedHmId(url: string | undefined) {
  const {domainResolver, universalClient: client} = useUniversalAppContext()
  const directId = useMemo(() => unpackHmId(url), [url])
  const needsResolution = !!url?.startsWith('hm://') && !!directId?.uid.includes('.')
  const resolution = useQuery({
    queryKey: ['resolved-hm-id', url],
    enabled: needsResolution,
    queryFn: () =>
      resolveId(url!, {
        domainResolver:
          domainResolver ||
          (async (domain) => {
            const info = await client.request('GetDomain', {domain})
            return info.registeredAccountUid
              ? {registeredAccountUid: info.registeredAccountUid, isGateway: info.isGateway}
              : null
          }),
      }),
    retry: false,
  })
  return {
    id: needsResolution ? resolution.data : directId,
    isLoading: needsResolution && resolution.isInitialLoading,
    error: needsResolution ? resolution.error : null,
  }
}

export function useResolvedResource(
  id: UnpackedHypermediaId | null | undefined,
  options?: UseQueryOptions<HMResolvedResource | null>,
) {
  const client = useUniversalClient()
  const version = id?.version || undefined
  return useQuery({
    enabled: options?.enabled ?? !!id,
    queryKey: [queryKeys.RESOLVED_ENTITY, id?.id, version],
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMResolvedResource | null> => {
      if (!id) return null

      // Redirects are daemon-served data, so the chain can be cyclic — bound the walk
      // instead of recursing, or a cycle spins forever.
      const visited = new Set<string>()
      let current = id
      while (true) {
        if (visited.has(current.id)) {
          throw new Error(`Redirect cycle detected while resolving ${id.id}`)
        }
        if (visited.size > MAX_REDIRECT_HOPS) {
          throw new Error(`Too many redirects while resolving ${id.id} (limit ${MAX_REDIRECT_HOPS})`)
        }
        visited.add(current.id)
        const resource = await client.request('Resource', current, {signal})
        if (resource?.type === 'redirect') {
          current = resource.redirectTarget
          continue
        }
        return resource as HMResolvedResource
      }
    },
    ...options,
  })
}

type DiscoveryStateInfo = {
  isDiscovering: boolean | undefined
  isTombstone: boolean
  isNotFound: boolean
}

/** Hook to get discovery states for multiple entity IDs. */
function useDiscoveryStates(entityIds: (string | undefined)[]): DiscoveryStateInfo[] {
  const client = useUniversalClient()

  // Create a stable key for the ids array
  const idsKey = entityIds.join(',')

  // Subscribe to all discovery streams
  const streams = useMemo(() => {
    if (!client.discovery) return []
    return entityIds.map((id) => (id ? client.discovery!.getDiscoveryStream(id) : undefined))
  }, [client.discovery, idsKey])

  // Get current values from all streams
  // isDiscovering is undefined when no discovery state has been received yet,
  // matching useDiscoveryState behavior so callers can distinguish "unknown" from "not discovering"
  const [states, setStates] = useState<DiscoveryStateInfo[]>(() =>
    streams.map((stream) => {
      const state = stream?.get()
      const isTimedOut = state ? Date.now() - state.startedAt > DISCOVERY_TIMEOUT_MS : false
      return {
        isDiscovering: state ? state.isDiscovering && !isTimedOut : undefined,
        isTombstone: state?.isTombstone ?? false,
        isNotFound: state?.isNotFound ?? false,
      }
    }),
  )

  useEffect(() => {
    if (!client.discovery) return

    const cleanups = streams.map((stream, index) => {
      if (!stream) return undefined
      return stream.subscribe((state) => {
        setStates((prev) => {
          const next = [...prev]
          const isTimedOut = state ? Date.now() - state.startedAt > DISCOVERY_TIMEOUT_MS : false
          next[index] = {
            isDiscovering: state ? state.isDiscovering && !isTimedOut : undefined,
            isTombstone: state?.isTombstone ?? false,
            isNotFound: state?.isNotFound ?? false,
          }
          return next
        })
      })
    })

    return () => cleanups.forEach((cleanup) => cleanup?.())
  }, [streams, client.discovery])

  return states
}

export function useResources(
  ids: (UnpackedHypermediaId | null | undefined)[],
  options?: UseQueryOptions<HMResource | null> & {
    subscribed?: boolean
    recursive?: boolean
  },
) {
  const client = useUniversalClient()
  const {subscribed, recursive, ...queryOptions} = options ?? {}

  // Discovery subscription (desktop only)
  useEffect(() => {
    if (!subscribed || !client.subscribeEntity) return
    const cleanups = ids
      .filter((id): id is UnpackedHypermediaId => !!id)
      .map((id) => client.subscribeEntity!({id, recursive}))
    return () => cleanups.forEach((cleanup) => cleanup())
  }, [subscribed, recursive, ids.map((id) => id?.id).join(','), client.subscribeEntity])

  // Get discovery states - only when subscribed to avoid unnecessary re-renders
  const entityIdStrings = subscribed ? ids.map((id) => id?.id) : []
  const discoveryStates = useDiscoveryStates(entityIdStrings)

  const queryResults = useQueries({
    queries: ids.map((id) => ({
      ...queryResource(client, id),
      ...queryOptions,
    })),
  })

  // Combine query results with discovery state
  const hasDiscoveryService = !!client.discovery
  return queryResults.map((result, index) => {
    const {
      isDiscovering: discoveryInProgress,
      isTombstone,
      isNotFound,
    } = discoveryStates[index] ?? {
      isDiscovering: false,
      isTombstone: false,
      isNotFound: false,
    }
    // Show discovering when: subscribed, not-found, AND either discovery in progress OR query is fetching
    // BUT: never show discovering UI for tombstoned or settled not-found resources
    // discoveryInProgress === undefined means we haven't received discovery state yet (the
    // subscription round-trip hasn't completed). Since we're subscribed and discovery will start,
    // treat this gap as discovering rather than flashing "not found" prematurely.
    // However, on web there is no discovery service, so discoveryInProgress is permanently
    // undefined — don't treat that as "discovering" or profiles will never resolve.
    const isDiscovering =
      !!subscribed &&
      !isTombstone &&
      !isNotFound &&
      result.data?.type === 'not-found' &&
      (!!discoveryInProgress || result.isFetching || (hasDiscoveryService && discoveryInProgress === undefined))
    return {
      ...result,
      isDiscovering,
      isTombstone,
    }
  })
}

export function useAccounts(ids: (string | null | undefined)[], options?: UseQueryOptions<HMMetadataPayload | null>) {
  const client = useUniversalClient()
  return useQueries({
    queries: ids.map((id) => ({
      ...queryAccount(client, id),
      ...options,
    })),
  })
}

export type HMAccountsMetadataResult = {
  data: HMAccountsMetadata
  isLoading: boolean
}

export function useAccountsMetadata(uids: string[]): HMAccountsMetadataResult {
  const client = useUniversalClient()
  const results = useQueries({
    queries: uids.map((uid) => queryAccount(client, uid)),
  })
  const isLoading = results.some((r) => r.isLoading)
  const data: HMAccountsMetadata = {}
  results.forEach((result, index) => {
    const uid = uids[index]
    if (result.data && uid) {
      data[uid] = result.data
    }
  })
  return {data, isLoading}
}

export class HMError extends Error {}

export class HMRedirectError extends HMError {
  constructor(public redirect: RedirectErrorDetails) {
    super('Document Redirected')
  }
  public get target(): UnpackedHypermediaId {
    return hmId(this.redirect.targetAccount, {
      path: entityQueryPathToHmIdPath(this.redirect.targetPath),
    })
  }
  public get republish(): boolean {
    return !!this.redirect.republish
  }
}

export class HMNotFoundError extends HMError {
  constructor() {
    super('Resource Not Found')
  }
}

export class HMResourceTombstoneError extends HMError {
  constructor() {
    super('Resource has been Deleted')
  }
}

// @ts-ignore
export function getErrorMessage(err: any) {
  try {
    const e = ConnectError.from(err)
    if (e.code === Code.NotFound) {
      return new HMNotFoundError()
    }
    if (e.code === Code.FailedPrecondition && e.message.match('marked as deleted')) {
      return new HMResourceTombstoneError()
    }
    if (e.code === Code.Unknown && e.message.match('ipld: could not find')) {
      // the API has a lot of different types for "not found"!
      return new HMNotFoundError()
    }
    if (e.code === Code.Unknown && e.message.match('not found')) {
      // the message is like: "cid 1234 not found", I think we hit this for specific versions.
      // the API has a lot of different types for "not found"!
      return new HMNotFoundError()
    }

    const firstDetail = e.details[0]
    if (
      firstDetail &&
      // @ts-expect-error
      firstDetail.type === 'com.seed.documents.v3alpha.RedirectErrorDetails'
    ) {
      const redirect = RedirectErrorDetails.fromBinary(
        // @ts-expect-error
        firstDetail.value as Uint8Array,
      )
      return new HMRedirectError(redirect)
    }

    // Fallback: detect redirect from message when error details are missing
    if (e.code === Code.FailedPrecondition && e.message.match('has a redirect to')) {
      const match = e.message.match(/has a redirect to (hm:\/\/\S+?)(?: \(republish = (true|false)\))?$/)
      if (match) {
        const targetId = unpackHmId(match[1])
        if (targetId) {
          return new HMRedirectError(
            new RedirectErrorDetails({
              targetAccount: targetId.uid,
              targetPath: targetId.path ? `/${targetId.path.join('/')}` : '',
              republish: match[2] === 'true',
            }),
          )
        }
      }
    }
  } catch (e) {
    return e
  }
}

export function useSelectedAccountId() {
  const {selectedIdentity} = useUniversalAppContext()
  return useStream(selectedIdentity) ?? null
}

/**
 * Returns true if the given UID belongs to the current user.
 * Checks both the selected identity (vault/account UID) and the signing
 * identity (web session key UID) to handle vault delegation on web.
 */
export function useIsCurrentUser(uid: string | null | undefined): boolean {
  const {selectedIdentity, signingIdentity} = useUniversalAppContext()
  const selectedId = useStream(selectedIdentity) ?? null
  const signingId = useStream(signingIdentity) ?? null
  if (!uid) return false
  return uid === selectedId || uid === signingId
}

export function useDirectory(
  id: UnpackedHypermediaId | null | undefined,
  options?: {mode?: 'Children' | 'AllDescendants'},
) {
  const client = useUniversalClient()
  const mode = options?.mode || 'Children'
  return useQuery(queryDirectory(client, id, mode))
}

export function useDirectoryWithDrafts(
  id: UnpackedHypermediaId | null | undefined,
  options?: {mode?: 'Children' | 'AllDescendants'},
) {
  const directory = useDirectory(id, options)
  const drafts = useDraftsForAccountSafe(id?.uid)

  return useMemo(() => {
    return {
      directory: directory.data,
      drafts: drafts.data ?? [],
      isLoading: directory.isLoading || drafts.isLoading,
      isInitialLoading: directory.isInitialLoading || drafts.isLoading,
    }
  }, [directory.data, drafts.data, directory.isLoading, drafts.isLoading, directory.isInitialLoading])
}

export function useRootDocuments() {
  const client = useUniversalClient()
  return useQuery({
    queryKey: [queryKeys.ROOT_DOCUMENTS],
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMListAccountsOutput> => {
      return await client.request('ListAccounts', undefined, {signal})
    },
  })
}

export function useCID(cid: string | undefined) {
  const client = useUniversalClient()
  return useQuery({
    queryKey: [queryKeys.CID, cid],
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMGetCIDOutput> => {
      return await client.request('GetCID', {cid: cid!}, {signal})
    },
    enabled: !!cid,
    // A blob that isn't found (yet) is a state every consumer renders
    // (searching/retry UIs) — never a reason to unmount the page via the
    // QueryClient's default useErrorBoundary: true.
    useErrorBoundary: false,
  })
}

export function useComments(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  return useQuery(queryComments(client, id))
}

export function useAuthoredComments(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  const isRootAccount = !id?.path?.filter((p) => !!p).length
  return useQuery({
    queryKey: [queryKeys.AUTHORED_COMMENTS, id?.id],
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMListCommentsByAuthorOutput> => {
      if (!id) throw new Error('ID required')
      return await client.request('ListCommentsByAuthor', {authorId: id}, {signal})
    },
    enabled: !!id && isRootAccount,
  })
}

export function useCitations(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  return useQuery({
    ...queryCitations(client, id),
    useErrorBoundary: false,
  })
}

export function useChanges(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  return useQuery({
    ...queryChanges(client, id),
    useErrorBoundary: false,
  })
}

export function useCapabilities(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  // Skip the capability lookup for anonymous pending drafts.
  const targetId = isPendingSpaceUid(id?.uid) ? undefined : id
  return useQuery(queryCapabilities(client, targetId))
}

export function useCollaborators(docId: UnpackedHypermediaId) {
  const collaborators = useDocumentCollaborators(docId)
  const accounts = collaborators.data?.accounts || {}

  return {
    parentCapabilities:
      collaborators.data?.parentCapabilities.filter((capability) => accounts[capability.accountUid]) || [],
    grantedCapabilities:
      collaborators.data?.grantedCapabilities.filter((capability) => accounts[capability.accountUid]) || [],
    publisherUid: collaborators.data?.publisherUid || docId.uid,
    accounts,
    isLoading: collaborators.isLoading,
    isInitialLoading: collaborators.isInitialLoading,
  }
}

export function useSiteMembers(id: UnpackedHypermediaId | null | undefined) {
  const collaborators = useDocumentCollaborators(id)
  const accounts = collaborators.data?.accounts || {}

  return {
    grantedMembers: collaborators.data?.grantedMembers.filter((member) => accounts[member.account.uid]) || [],
    members: collaborators.data?.members.filter((member) => accounts[member.account.uid]) || [],
    accounts,
    isLoading: collaborators.isLoading,
    isInitialLoading: collaborators.isInitialLoading,
  }
}

export function useDocumentCollaborators(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  const result = useQuery(queryDocumentCollaborators(client, id))
  const collaboratorAccountUids = useMemo(() => {
    const data = result.data
    if (!data) return []
    const uids = new Set<string>([data.publisherUid])
    data.parentCapabilities.forEach((capability) => uids.add(capability.accountUid))
    data.grantedCapabilities.forEach((capability) => uids.add(capability.accountUid))
    data.grantedMembers.forEach((member) => uids.add(member.account.uid))
    data.members.forEach((member) => uids.add(member.account.uid))
    return Array.from(uids)
  }, [result.data])
  useAccountSubscriptions(collaboratorAccountUids)
  return result
}

export function useInfiniteFeed(pageSize: number = 10) {
  const client = useUniversalClient()
  return useInfiniteQuery({
    queryKey: [queryKeys.FEED, 'infinite', pageSize],
    queryFn: async ({
      pageParam,
      signal,
    }: {pageParam?: unknown; signal?: AbortSignal} = {}): Promise<HMListEventsOutput> => {
      return await client.request(
        'ListEvents',
        {
          pageSize,
          pageToken: pageParam as string | undefined,
        },
        {signal},
      )
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    refetchInterval: 30000,
  })
}

export function useLatestEvent() {
  const client = useUniversalClient()
  return useQuery({
    queryKey: [queryKeys.FEED, 'latest'],
    queryFn: async ({signal}: {signal?: AbortSignal} = {}) => {
      const result = await client.request(
        'ListEvents',
        {
          pageSize: 1,
        },
        {signal},
      )
      return result.events[0] || null
    },
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  })
}

export function useChildrenList(id: UnpackedHypermediaId | null | undefined) {
  const client = useUniversalClient()
  return useQuery(queryDirectory(client, id, 'Children'))
}

export function extractIpfsUrlCid(cidOrIPFSUrl: string): string | null {
  const regex = /^ipfs:\/\/(.+)$/
  const match = cidOrIPFSUrl.match(regex)
  return match?.[1] ?? null
}

export type HypermediaSearchResult = {
  destination?: string
  errorMessage?: string
}

export async function search(input: string): Promise<HypermediaSearchResult> {
  const normalizedInput = input.trim()
  const cid = extractIpfsUrlCid(normalizedInput)
  if (cid) {
    return {destination: `/ipfs/${cid}`}
  }

  // Strip any trailing site view term (e.g. /:comments, /:profile) so the
  // underlying resource resolves cleanly, then re-attach it to the destination
  // so the explorer opens the matching view. `unpackHmId` already resolves the
  // `/:comments/<id>` form into the comment resource, so keep the raw input in
  // that case and don't append a redundant view term.
  const {url: cleanedInput, viewTerm, commentId, accountUid} = extractViewTermFromUrl(normalizedInput)
  const idInput = commentId ? normalizedInput : cleanedInput
  const passViewTerm = commentId ? null : viewTerm

  // A profile-family view term can name the account it applies to
  // (e.g. site.com/:profile/<accountUid>). That targets the named account's
  // profile directly — not the site host it happened to be viewed on — so it
  // must win over resolving the hostname below.
  if (accountUid) {
    return {
      destination: createWebHMUrl(accountUid, {hostname: null, viewTerm: passViewTerm}),
    }
  }

  const unpackedId = unpackHmId(idInput)
  if (unpackedId) {
    return {
      destination: createWebHMUrl(unpackedId.uid, {
        path: unpackedId.path,
        version: unpackedId.version,
        latest: unpackedId.latest,
        blockRef: unpackedId.blockRef,
        blockRange: unpackedId.blockRange,
        hostname: null,
        viewTerm: passViewTerm,
      }),
    }
  }

  if (cleanedInput.match(/\./)) {
    // it might be a url
    const hasProtocol = cleanedInput.match(/^https?:\/\//)
    const searchUrl = hasProtocol ? cleanedInput : `https://${cleanedInput}`

    try {
      const resolved = await resolveHypermediaUrl(searchUrl)
      if (resolved?.hmId) {
        return {
          destination: createWebHMUrl(resolved.hmId.uid, {
            path: resolved.hmId.path,
            version: resolved.hmId.version,
            latest: resolved.hmId.latest,
            blockRef: resolved.hmId.blockRef,
            blockRange: resolved.hmId.blockRange,
            hostname: null,
            viewTerm: passViewTerm,
          }),
        }
      }
    } catch {
      // Fall through to the generic invalid-input error below.
    }
  }
  return {
    errorMessage: 'Invalid input. Please enter a valid hypermedia URL or IPFS url.',
  }
}
